import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const Pricing = () => {
  const plans = [
    {
      name: 'Basic Plan',
      subtitle: 'For Startups & Individuals',
      oldPrice: '149',
      price: '75',
      features: [
        '1 Press Release Distribution',
        'Featured in Ixoraly Newsroom',
        'Search Engine Indexing',
        'Social Media Sharing'
      ],
      popular: false
    },
    {
      name: 'Growth Plan',
      subtitle: 'For SME\'s & Agencies',
      oldPrice: '499',
      price: '250',
      features: [
        'Everything in Basic',
        'Email Distribution to 500+ Journalists',
        'Priority Placement in Ixoraly Newsroom',
        '1 Image Included in PR',
        'Distribution Report'
      ],
      popular: true
    },
    {
      name: 'Premium Plan',
      subtitle: 'For Corporates & Global Brands',
      oldPrice: '999',
      price: '500',
      features: [
        'Everything in Growth',
        'Extended Distribution to Partner Websites',
        'Google News Approval',
        'Multimedia Support (Images & Videos)',
        'Dedicated Account Manager',
        'Priority Support'
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'How quickly will my press release be distributed?',
      answer: 'Standard distribution typically happens within 24-48 hours. Growth and Premium plans receive priority processing.'
    },
    {
      question: 'Can I include images and videos in my press release?',
      answer: 'Yes! The Growth plan includes 1 image, and Premium plan supports full multimedia including videos.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Refunds are not available once distribution has begun. Please review your content carefully before submission.'
    },
    {
      question: 'How do I track my press release performance?',
      answer: 'Growth and Premium plans include detailed distribution reports with analytics and metrics.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pricing"
        description="Affordable press release distribution plans starting at $149. Choose Basic, Growth, or Premium for maximum media coverage and SEO visibility."
        path="/pricing"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="pricing-heading">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Choose a plan that fits your communication needs. Pay only for what you need — no hidden costs!
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                data-testid={`pricing-plan-${index}`}
                className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  plan.popular 
                    ? 'border-2 border-navy-600 shadow-xl scale-105' 
                    : 'border-2 border-gray-100 hover:border-navy-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-navy-600 text-white py-2 text-center text-sm font-bold flex items-center justify-center space-x-2">
                    <Star size={16} fill="currentColor" />
                    <span>Most Popular</span>
                  </div>
                )}
                <div className={`p-8 ${plan.popular ? 'pt-14' : ''}`}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mb-6">{plan.subtitle}</p>
                  <div className="mb-8">
                    <span className="text-2xl text-gray-400 line-through mr-2">${plan.oldPrice}</span>
                    <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-600"> per PR</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start space-x-3">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="text-green-600" size={14} />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button
                      data-testid={`pricing-cta-${index}`}
                      className={`w-full py-6 text-base font-semibold ${
                        plan.popular
                          ? 'bg-navy-600 hover:bg-navy-700 text-white'
                          : 'bg-white border-2 border-navy-600 text-navy-600 hover:bg-navy-50'
                      }`}
                    >
                      Get Started
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Plan */}
      <section className="py-16 bg-navy-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need a Custom Plan?</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Have a unique requirement? We understand that one size doesn't fit all. Whether you need bulk press release distribution, region-specific targeting, or a tailored media list — we can craft a custom plan just for you.
            </p>
            <Link to="/contact">
              <Button
                data-testid="custom-plan-cta"
                className="bg-coral-600 hover:bg-coral-700 text-white px-8 py-4 text-lg font-semibold"
              >
                Contact Us for Custom Pricing
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about our pricing</p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-navy-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="text-navy-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Amplify Your Message?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join thousands of businesses that trust Ixoraly for their press release distribution needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-press-release"
              className="inline-flex items-center justify-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Submit Your Press Release
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-200 border border-white/20"
            >
              Contact Sales Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
